import { create } from 'zustand';
import { User } from '@/types';
import { authApi } from '@/lib/api';

const TOKEN_KEYS = ['access_token', 'refresh_token'] as const;
const IMPERSONATE_BACKUP_KEY = 'impersonate_backup';

/// Dónde viven los tokens de la sesión: `local` (localStorage, "recuérdame",
/// compartido por todas las pestañas) o `session` (sessionStorage, propio
/// de cada pestaña — login sin "recuérdame" e impersonaciones).
export type TokenStorageKind = 'local' | 'session';

function storageFor(kind: TokenStorageKind): Storage {
  return kind === 'local' ? localStorage : sessionStorage;
}

export function isImpersonating(): boolean {
  return typeof window !== 'undefined'
    && sessionStorage.getItem(IMPERSONATE_BACKUP_KEY) !== null;
}

/// Lee un token dando prioridad a sessionStorage. Así una impersonación
/// (que siempre vive en sessionStorage, por pestaña) se impone a la sesión
/// "recuérdame" de localStorage SIN tener que borrar esta última.
export function getStoredToken(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const inSession = sessionStorage.getItem(key);
  // Durante una impersonación la sesión de la pestaña es SOLO la de
  // sessionStorage: nunca completar con un token del original.
  if (inSession !== null || isImpersonating()) return inSession;
  return localStorage.getItem(key);
}

/// Storage en el que está guardada la sesión activa de esta pestaña.
export function getActiveTokenStorage(): TokenStorageKind {
  if (typeof window === 'undefined') return 'session';
  const inSession = TOKEN_KEYS.some((k) => sessionStorage.getItem(k) !== null);
  if (inSession) return 'session';
  const inLocal = TOKEN_KEYS.some((k) => localStorage.getItem(k) !== null);
  return inLocal ? 'local' : 'session';
}

export function storeTokens(
  kind: TokenStorageKind,
  accessToken: string,
  refreshToken?: string | null,
) {
  if (typeof window === 'undefined') return;
  const storage = storageFor(kind);
  storage.setItem('access_token', accessToken);
  if (refreshToken) storage.setItem('refresh_token', refreshToken);
}

function clearTokensIn(kind: TokenStorageKind) {
  TOKEN_KEYS.forEach((k) => storageFor(kind).removeItem(k));
}

/// Cierre de sesión completo de esta pestaña: tokens de ambos storages
/// e impersonación en curso.
export function clearStoredTokens() {
  if (typeof window === 'undefined') return;
  clearTokensIn('local');
  clearTokensIn('session');
  sessionStorage.removeItem(IMPERSONATE_BACKUP_KEY);
}

/// El backend ha rechazado la sesión activa de esta pestaña (401/403 sin
/// refresh posible). Si era una impersonación, se descarta solo ella y la
/// pestaña vuelve a la sesión original. Si no, se limpia todo.
/// Devuelve `true` si tras descartar aún queda una sesión utilizable.
export function discardCurrentSession(): boolean {
  if (typeof window === 'undefined') return false;
  const backup = getImpersonateBackup();
  if (!backup) {
    clearStoredTokens();
    return false;
  }
  clearTokensIn('session');
  sessionStorage.removeItem(IMPERSONATE_BACKUP_KEY);
  if (backup.tokenStorage === 'session') {
    storeTokens('session', backup.accessToken, backup.refreshToken);
  }
  return getStoredToken('access_token') !== null;
}

interface ImpersonateBackup {
  accessToken: string;
  refreshToken: string;
  user: User;
  /// Storage del que salieron los tokens originales, para devolverlos al
  /// mismo sitio al terminar. Backups antiguos no lo traen → `session`.
  tokenStorage: TokenStorageKind;
}

export function getImpersonateBackup(): ImpersonateBackup | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(IMPERSONATE_BACKUP_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ImpersonateBackup>;
    if (typeof parsed.accessToken !== 'string' || !parsed.user) return null;
    return {
      accessToken: parsed.accessToken,
      refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : '',
      user: parsed.user,
      tokenStorage: parsed.tokenStorage === 'local' ? 'local' : 'session',
    };
  } catch {
    return null;
  }
}

/// Snapshot de la sesión original de esta pestaña, listo para guardarse
/// como backup antes de impersonar.
function captureCurrentSession(): ImpersonateBackup | null {
  const accessToken = getStoredToken('access_token');
  const user = useAuthStore.getState().user;
  if (!accessToken || !user) return null;
  return {
    accessToken,
    refreshToken: getStoredToken('refresh_token') ?? '',
    user,
    tokenStorage: getActiveTokenStorage(),
  };
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  avatarVersion: number;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  loginWithGoogle: (idToken: string, rememberMe: boolean) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  setUser: (user: User) => void;
  bumpAvatarVersion: () => void;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  avatarVersion: 0,

  login: async (email: string, password: string, rememberMe: boolean) => {
    const response = await authApi.login({ email, password });
    storeTokens(rememberMe ? 'local' : 'session', response.accessToken, response.refreshToken);
    set({ user: response.user, isAuthenticated: true });
  },

  loginWithGoogle: async (idToken: string, rememberMe: boolean) => {
    const response = await authApi.googleLogin(idToken);
    storeTokens(rememberMe ? 'local' : 'session', response.accessToken, response.refreshToken);
    set({ user: response.user, isAuthenticated: true });
  },

  register: async (name: string, email: string, password: string) => {
    const response = await authApi.register({ name, email, password });
    storeTokens('local', response.accessToken, response.refreshToken);
    set({ user: response.user, isAuthenticated: true });
  },

  logout: async () => {
    await authApi.logout();
    clearStoredTokens();
    set({ user: null, isAuthenticated: false });
  },

  setUser: (user: User) => set({ user }),

  bumpAvatarVersion: () => set((state) => ({ avatarVersion: state.avatarVersion + 1 })),

  startImpersonation: async (userId: string) => {
    // Bloquear impersonación anidada — si ya hay backup, estamos impersonando
    if (isImpersonating()) {
      throw new Error('Ya estás en una sesión de impersonación');
    }
    const original = captureCurrentSession();
    if (!original) {
      throw new Error('No hay una sesión activa que respaldar');
    }
    // La llamada a la API va ANTES de guardar el backup: si falla (403,
    // red...), no debe quedar un backup huérfano en sessionStorage.
    const response = await authApi.impersonate(userId);
    sessionStorage.setItem(IMPERSONATE_BACKUP_KEY, JSON.stringify(original));
    // Los tokens de impersonación siempre en sessionStorage (por pestaña).
    // `getStoredToken` prioriza sessionStorage, así que la sesión
    // "recuérdame" de localStorage sigue intacta para otras pestañas.
    storeTokens('session', response.accessToken, response.refreshToken);
    // El backend devuelve `impersonatorId` en `/auth/me`; se anota ya aquí
    // para que el banner no espere al siguiente checkAuth.
    set({
      user: { ...response.user, impersonatorId: original.user.id },
      isAuthenticated: true,
    });
  },

  stopImpersonation: () => {
    const backup = getImpersonateBackup();
    if (!backup) return;
    clearTokensIn('session');
    sessionStorage.removeItem(IMPERSONATE_BACKUP_KEY);
    // Restaurar la sesión original en el MISMO storage del que salió. Si
    // vivía en localStorage nunca se tocó: solo se repone si ya no queda
    // nada ahí (p. ej. logout en otra pestaña).
    if (backup.tokenStorage === 'session' || !localStorage.getItem('access_token')) {
      storeTokens(backup.tokenStorage, backup.accessToken, backup.refreshToken);
    }
    set({ user: backup.user, isAuthenticated: true });
  },

  checkAuth: async () => {
    const token = getStoredToken('access_token');
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const user = await authApi.getProfile();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (err: unknown) {
      // Solo borrar tokens en errores de auth explícitos (401/403).
      // Errores de red o del servidor (5xx) no deben destruir una sesión válida.
      const status =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 401 || status === 403) {
        // Si era una impersonación caducada, se descarta y se revalida la
        // sesión original en lugar de echar al usuario al login.
        if (discardCurrentSession()) {
          await get().checkAuth();
          return;
        }
        set({ user: null, isAuthenticated: false, isLoading: false });
      } else {
        // Backend temporalmente no disponible — mantener tokens, no cerrar sesión.
        set({ isLoading: false, isAuthenticated: false });
      }
    }
  },
}));
